import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate, useNavigate } from "react-router";
import { Link as LinkIcon } from "lucide-react";
import type { inferRouterOutputs } from "@trpc/server";
import { z } from "zod";
import type { AppRouter } from "@runcheetah/api/router";
import type { OnboardingStage } from "@runcheetah/shared";

import { CenteredLoader, InlineNotice, PageIntro } from "@/components/common";
import { BrandOnboardingAnalyzeStage } from "@/components/onboarding/brand-onboarding-analyze-stage";
import { BrandOnboardingChatStage } from "@/components/onboarding/brand-onboarding-chat-stage";
import { BrandOnboardingConfirmStage } from "@/components/onboarding/brand-onboarding-confirm-stage";
import { BrandOnboardingGeneratingStage } from "@/components/onboarding/brand-onboarding-generating-stage";
import { buildBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import {
  deriveAudienceCards,
  hasPositioningData,
  mergeAnalyzeStepSummaries,
} from "@/lib/brand-onboarding-display";
import { trpc } from "@/lib/trpc";
import { Button } from "@runcheetah/ui";
import { Card, CardContent } from "@runcheetah/ui";
import { Input } from "@runcheetah/ui";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type OnboardingSummary = RouterOutputs["brand"]["getById"]["onboarding"];
type AnalysisStepSummary = OnboardingSummary["analysisSteps"][number];
type AnalyzePreviewData = {
  profile: RouterOutputs["brand"]["getBrandProfile"] | null;
  voice: RouterOutputs["brand"]["getBrandVoice"] | null;
  visual: RouterOutputs["brand"]["getVisualStyle"] | null;
};

const READY_REVEAL_MS = 1_400;

const STAGE_RANK: Record<string, number> = {
  queued: 0,
  processing_origin: 1,
  processing_pages: 2,
  extracting_facts: 3,
  extracting_visual_identity: 4,
  ready: 5,
  failed: -1,
};

const stageRank = (stage: OnboardingStage | null): number => (stage ? (STAGE_RANK[stage] ?? 0) : 0);

const analysisStepsEqual = (
  previous: AnalysisStepSummary[] | null | undefined,
  incoming: AnalysisStepSummary[] | null | undefined,
): boolean => {
  if ((previous?.length ?? 0) !== (incoming?.length ?? 0)) return false;
  const previousByKey = new Map((previous ?? []).map((step) => [step.key, step.status] as const));
  return (incoming ?? []).every((step) => previousByKey.get(step.key) === step.status);
};

const hasHydratedValue = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined;
};

const mergeHydratedPreviewPart = <T extends Record<string, unknown>>(
  queryData: T | null | undefined,
  liveData: T | null | undefined,
): T | null => {
  if (!queryData && !liveData) return null;

  return {
    ...queryData,
    ...Object.fromEntries(
      Object.entries(liveData ?? {}).filter(([, value]) => hasHydratedValue(value)),
    ),
  } as T;
};

const hasBrandProfilePreviewData = (
  profile: RouterOutputs["brand"]["getBrandProfile"] | null | undefined,
): boolean =>
  Boolean(profile && hasPositioningData(profile) && deriveAudienceCards(profile).length > 0);

const hasBrandVoicePreviewData = (
  voice: RouterOutputs["brand"]["getBrandVoice"] | null | undefined,
): boolean =>
  Boolean(
    voice?.tone ||
    voice?.voice ||
    voice?.vocabularyStyle ||
    voice?.sentenceComplexity ||
    voice?.contentDnaHumorLevel ||
    voice?.culturalReferences ||
    voice?.wordsToAvoid?.length ||
    voice?.wordsToEmphasize?.length ||
    voice?.contentDnaPrimaryStyle ||
    voice?.contentDnaSecondaryStyle ||
    voice?.contentDnaConfidence ||
    voice?.socialMechanics?.length ||
    voice?.socialTruths?.length ||
    voice?.audienceTensions?.length ||
    voice?.contentAngles?.length,
  );

const hasVisualPreviewData = (
  visual: RouterOutputs["brand"]["getVisualStyle"] | null | undefined,
): boolean => Boolean(visual?.colorPalette?.length);

const urlSchema = z
  .string()
  .trim()
  .transform((value) => (!/^https?:\/\//i.test(value) ? `https://${value}` : value))
  .pipe(z.url())
  .refine((value) => new URL(value).hostname.split(".").filter(Boolean).length >= 2);

function BrandUrlForm({
  title,
  description,
  initialValue = "",
  submitLabel,
  isPending,
  error,
  onSubmit,
}: {
  title: string;
  description?: string;
  initialValue?: string;
  submitLabel: string;
  isPending: boolean;
  error: string;
  onSubmit: (sourceUrl: string) => void;
}) {
  const intl = useIntl();
  const [value, setValue] = useState(initialValue);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError("");
    const result = urlSchema.safeParse(value);
    if (!result.success) {
      setLocalError(
        intl.formatMessage({
          id: "brandOnboarding.invalidUrl",
          defaultMessage: "Please enter a valid website URL.",
        }),
      );
      return;
    }

    onSubmit(result.data);
  };

  return (
    <div className="mx-auto flex min-h-[70vh] w-full max-w-2xl items-center px-6 py-8">
      <Card className="w-full overflow-hidden rounded-3xl border-border/80 shadow-soft">
        <CardContent className="space-y-6 p-6 sm:p-8">
          <PageIntro title={title} description={description} />
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex items-center gap-2 rounded-full border bg-background p-2 shadow-soft transition-shadow focus-within:shadow-deep focus-within:ring-2 focus-within:ring-peach/50">
              <LinkIcon className="ml-3 h-5 w-5 shrink-0 text-muted-foreground" />
              <Input
                type="text"
                placeholder={intl.formatMessage({
                  id: "brandOnboarding.urlPlaceholder",
                  defaultMessage: "paste your website url...",
                })}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                className="flex-1 border-0 bg-transparent text-sm shadow-none placeholder:text-muted-foreground focus-visible:ring-0"
                disabled={isPending}
              />
              <Button type="submit" className="rounded-full px-5 text-sm" disabled={isPending}>
                {isPending
                  ? intl.formatMessage({
                      id: "common.loadingEllipsis",
                      defaultMessage: "Loading...",
                    })
                  : submitLabel}
              </Button>
            </div>
            {localError || error ? (
              <p className="text-sm text-destructive">{localError || error}</p>
            ) : null}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function AddBrandPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const listQuery = trpc.brand.listMine.useQuery(undefined, {
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const createBrand = trpc.brand.createMine.useMutation({
    onSuccess: async (result) => {
      await utils.brand.listMine.invalidate();
      await utils.brand.getById.invalidate({ brandId: result.brand.id });
      navigate(buildBrandPath(result.brand.id, "/onboarding"), { replace: true });
    },
  });

  if (listQuery.isPending) {
    return <CenteredLoader />;
  }

  if (!listQuery.data?.canCreate) {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-8">
        <InlineNotice>
          <FormattedMessage
            id="brandOnboarding.brandLimitNotice"
            defaultMessage="You can onboard up to {limit} brands right now. Switch brands from the sidebar or contact support if you need more."
            values={{ limit: listQuery.data?.limit ?? 5 }}
          />
        </InlineNotice>
      </div>
    );
  }

  return (
    <BrandUrlForm
      title={intl.formatMessage({
        id: "brandOnboarding.addBrandTitle",
        defaultMessage: "Add a new brand",
      })}
      submitLabel={intl.formatMessage({
        id: "brandOnboarding.buildProfile",
        defaultMessage: "Build Profile",
      })}
      isPending={createBrand.isPending}
      error={createBrand.error?.message ?? ""}
      onSubmit={(sourceUrl) => createBrand.mutate({ sourceUrl })}
    />
  );
}

export function BrandOnboardingPage() {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const { brand, onboarding, brandQuery } = useRouteBrand();
  const [readyStage, setReadyStage] = useState<"confirm" | "generating">("confirm");
  const [showReadyReveal, setShowReadyReveal] = useState(false);
  const [livePreview, setLivePreview] = useState<AnalyzePreviewData | null>(null);
  const previousCanEnterConfirmRef = useRef<boolean | null>(null);
  const [liveOnboarding, setLiveOnboarding] = useState<{
    status: "queued" | "processing" | "scanned" | "failed";
    stage: OnboardingStage | null;
    onboardingBootstrapBatchId: string | null;
    analysisSteps: AnalysisStepSummary[];
    firstAssetReady: boolean;
  } | null>(null);
  const shouldRehydrateAnalyzePreview = Boolean(brand?.id) && readyStage === "confirm";

  const retryBrand = trpc.brand.retryMine.useMutation({
    onSuccess: async (result) => {
      await utils.brand.listMine.invalidate();
      await utils.brand.getById.invalidate({ brandId: result.brand.id });
      await brandQuery.refetch();
    },
  });
  const brandProfileQuery = trpc.brand.getBrandProfile.useQuery(
    { brandId: brand?.id ?? "" },
    {
      enabled: Boolean(brand?.id),
      staleTime: 0,
      refetchInterval: (query) =>
        shouldRehydrateAnalyzePreview &&
        !hasBrandProfilePreviewData(
          query.state.data as RouterOutputs["brand"]["getBrandProfile"] | undefined,
        )
          ? 1000
          : false,
      refetchOnWindowFocus: true,
    },
  );
  const brandVoiceQuery = trpc.brand.getBrandVoice.useQuery(
    { brandId: brand?.id ?? "" },
    {
      enabled: Boolean(brand?.id),
      staleTime: 0,
      refetchInterval: (query) =>
        shouldRehydrateAnalyzePreview &&
        !hasBrandVoicePreviewData(
          query.state.data as RouterOutputs["brand"]["getBrandVoice"] | undefined,
        )
          ? 1000
          : false,
      refetchOnWindowFocus: true,
    },
  );
  const visualStyleQuery = trpc.brand.getVisualStyle.useQuery(
    { brandId: brand?.id ?? "" },
    {
      enabled: Boolean(brand?.id),
      staleTime: 0,
      refetchInterval: (query) =>
        shouldRehydrateAnalyzePreview &&
        !hasVisualPreviewData(
          query.state.data as RouterOutputs["brand"]["getVisualStyle"] | undefined,
        )
          ? 1000
          : false,
      refetchOnWindowFocus: true,
    },
  );
  const refetchAnalyzePreviewData = () => {
    if (!brand?.id) {
      return;
    }

    void Promise.all([
      brandProfileQuery.refetch(),
      brandVoiceQuery.refetch(),
      visualStyleQuery.refetch(),
    ]);
  };

  useEffect(() => {
    if (!showReadyReveal || !brand?.id) {
      return;
    }

    void brandProfileQuery.refetch();
    void brandVoiceQuery.refetch();
    void visualStyleQuery.refetch();
  }, [brand?.id, brandProfileQuery, brandVoiceQuery, showReadyReveal, visualStyleQuery]);

  useEffect(() => {
    if (!brand) {
      setLiveOnboarding(null);
      return;
    }

    const incoming = {
      status: brand.status,
      stage: onboarding?.stage ?? null,
      onboardingBootstrapBatchId: onboarding?.onboardingBootstrapBatchId ?? null,
      analysisSteps: onboarding?.analysisSteps ?? [],
      firstAssetReady: onboarding?.completion.firstAssetReady ?? false,
    };

    setLiveOnboarding((previous) => {
      // Always apply if no previous state (initial load).
      if (previous === null) return incoming;
      // Always apply once the persisted brand scan is complete.
      if (incoming.firstAssetReady) return incoming;
      // Never let a server refetch regress the stage below what the subscription already reported.
      if (stageRank(incoming.stage) < stageRank(previous.stage)) {
        return {
          ...previous,
          analysisSteps: mergeAnalyzeStepSummaries(previous.analysisSteps, incoming.analysisSteps),
        };
      }
      return {
        ...incoming,
        analysisSteps: mergeAnalyzeStepSummaries(previous.analysisSteps, incoming.analysisSteps),
      };
    });
  }, [
    brand,
    onboarding?.analysisSteps,
    onboarding?.completion.firstAssetReady,
    onboarding?.onboardingBootstrapBatchId,
    onboarding?.stage,
  ]);

  useEffect(() => {
    setReadyStage("confirm");
    setShowReadyReveal(false);
    setLivePreview(null);
    previousCanEnterConfirmRef.current = null;
  }, [brand?.id]);

  trpc.brand.statusUpdates.useSubscription(
    { brandId: brand?.id ?? "" },
    {
      enabled: !!brand?.id && !(onboarding?.completion.firstAssetReady ?? false),
      onData: (data) => {
        const preview = data.preview as AnalyzePreviewData;
        setLivePreview(preview);
        if (brand?.id) {
          if (preview.profile) {
            utils.brand.getBrandProfile.setData({ brandId: brand.id }, preview.profile);
          }
          if (preview.voice) {
            utils.brand.getBrandVoice.setData({ brandId: brand.id }, preview.voice);
          }
          if (preview.visual) {
            utils.brand.getVisualStyle.setData({ brandId: brand.id }, preview.visual);
          }
        }
        refetchAnalyzePreviewData();

        setLiveOnboarding((previous) => {
          const isSameEvent =
            previous?.status === data.status &&
            previous.stage === data.stage &&
            previous.onboardingBootstrapBatchId === data.onboardingBootstrapBatchId &&
            analysisStepsEqual(previous.analysisSteps, data.analysisSteps);

          if (isSameEvent) {
            return previous;
          }

          // Never regress to an earlier stage — subscription heartbeats can be stale.
          if (
            previous !== null &&
            data.status !== "scanned" &&
            stageRank(data.stage) < stageRank(previous.stage)
          ) {
            return {
              ...previous,
              analysisSteps: mergeAnalyzeStepSummaries(previous.analysisSteps, data.analysisSteps),
            };
          }

          void brandQuery.refetch();
          return {
            status: data.status,
            stage: data.stage,
            onboardingBootstrapBatchId: data.onboardingBootstrapBatchId,
            analysisSteps: mergeAnalyzeStepSummaries(previous?.analysisSteps, data.analysisSteps),
            firstAssetReady: previous?.firstAssetReady ?? false,
          };
        });
      },
      onError: () => {
        void brandQuery.refetch();
      },
    },
  );

  const currentStatus = liveOnboarding?.status ?? brand?.status ?? "queued";
  const currentStage = liveOnboarding?.stage ?? onboarding?.stage ?? null;
  const showOnboardingFailureScreen = currentStatus === "failed" || currentStage === "failed";
  const onboardingBootstrapBatchId =
    liveOnboarding?.onboardingBootstrapBatchId ?? onboarding?.onboardingBootstrapBatchId ?? null;
  const analysisSteps = liveOnboarding?.analysisSteps ?? onboarding?.analysisSteps ?? [];
  const estimatedReadyMinutes = onboarding?.estimatedReadyMinutes ?? null;
  const mergedPreview = {
    profile: mergeHydratedPreviewPart(brandProfileQuery.data, livePreview?.profile),
    voice: mergeHydratedPreviewPart(brandVoiceQuery.data, livePreview?.voice),
    visual: mergeHydratedPreviewPart(visualStyleQuery.data, livePreview?.visual),
  };
  const canEnterConfirmStage = currentStatus === "scanned";

  useEffect(() => {
    const couldEnterConfirm = previousCanEnterConfirmRef.current;
    previousCanEnterConfirmRef.current = canEnterConfirmStage;

    if (couldEnterConfirm === false && canEnterConfirmStage) {
      setShowReadyReveal(true);
      const timeout = window.setTimeout(() => {
        setShowReadyReveal(false);
      }, READY_REVEAL_MS);

      return () => window.clearTimeout(timeout);
    }

    return undefined;
  }, [canEnterConfirmStage]);

  if (brandQuery.isPending) {
    return <CenteredLoader />;
  }

  if (!brand) {
    return <Navigate to="/app" replace />;
  }

  if (!onboarding) {
    return <CenteredLoader />;
  }

  if (showOnboardingFailureScreen && brand.onboardingMode !== "chatbot") {
    return (
      <BrandUrlForm
        title={intl.formatMessage({
          id: "brandOnboarding.retryTitle",
          defaultMessage: "We couldn't finish onboarding",
        })}
        description={intl.formatMessage({
          id: "brandOnboarding.retryDescription",
          defaultMessage:
            "Try again. If it keeps happening, contact support and we'll help you get set up.",
        })}
        initialValue={brand.sourceUrl}
        submitLabel={intl.formatMessage({
          id: "brandOnboarding.retryProfile",
          defaultMessage: "Retry Profile",
        })}
        isPending={retryBrand.isPending}
        error={retryBrand.error?.message ?? ""}
        onSubmit={(sourceUrl) => retryBrand.mutate({ brandId: brand.id, sourceUrl })}
      />
    );
  }

  if (brand.onboardingMode === "chatbot") {
    return (
      <BrandOnboardingChatStage brand={brand} onboarding={onboarding} status={currentStatus} />
    );
  }

  if (readyStage === "generating") {
    return (
      <BrandOnboardingGeneratingStage
        brandId={brand.id}
        batchId={onboardingBootstrapBatchId}
        sourceDomain={brand.sourceDomain}
      />
    );
  }

  if (canEnterConfirmStage && !showReadyReveal) {
    return (
      <BrandOnboardingConfirmStage
        brandId={brand.id}
        sourceDomain={brand.sourceDomain}
        onContinue={() => setReadyStage("generating")}
      />
    );
  }

  return (
    <BrandOnboardingAnalyzeStage
      brandId={brand.id}
      createdAt={brand.createdAt}
      sourceDomain={brand.sourceDomain}
      stage={currentStage}
      status={currentStatus}
      profile={mergedPreview.profile}
      voice={mergedPreview.voice}
      visual={mergedPreview.visual}
      analysisSteps={analysisSteps}
      estimatedReadyMinutes={estimatedReadyMinutes}
    />
  );
}
